/* =========================================================
   Section: LC PDP S7 Nutrition — Modal + Flavor Sync

   MODAL
   -----
   Opens the FDA-familiar Nutrition Facts panel on
   [data-nutri-open] click. Closes on scrim / close-button /
   Escape. Body scroll locks while open. Rebinds after each
   flavor-sync swap since innerHTML replacement drops handlers.

   FLAVOR SYNC (Deferred #150 lands here)
   --------------------------------------
   On hero picker interaction (pill or pack click) the section
   re-fetches itself via Shopify's Section Rendering API using
   the newly-selected variant ID, then swaps its innerHTML with
   the fresh render. Zero liquid changes needed — the section's
   variant-key assign (`variant.options[0] | downcase`) resolves
   to the new flavor automatically when Shopify re-renders.

   The hero JS updates the hidden [data-variant-id] input's
   .value synchronously on click (no change event fires when JS
   sets .value programmatically), so we listen for pill/pack
   clicks on document and defer the fetch to the next task with
   setTimeout 0 — that queues after the hero's click handler
   completes and the variant input holds the new ID.

   Rapid clicks: an AbortController cancels the in-flight fetch
   when a new picker click lands, so only the most recent
   selection wins. Modal open state and body-scroll lock are
   preserved across the swap.
   ========================================================= */
(function () {
  'use strict';

  var initialRoot = document.querySelector('.lc-powder-pdp-nutrition');
  if (!initialRoot) return;

  var sectionWrapper = initialRoot.closest('[id^="shopify-section-"]');
  if (!sectionWrapper) return;
  var sectionId = sectionWrapper.id.replace('shopify-section-', '');

  // -------- Modal (rebinds after each section swap) --------
  function bindModal() {
    var openBtn = sectionWrapper.querySelector('[data-nutri-open]');
    var modal   = sectionWrapper.querySelector('[data-nutri-modal]');
    if (!openBtn || !modal) return;
    var closers = modal.querySelectorAll('[data-nutri-close]');

    function setOpen(open) {
      modal.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.style.overflow = open ? 'hidden' : '';
    }

    openBtn.addEventListener('click', function () { setOpen(true); });
    closers.forEach(function (el) {
      el.addEventListener('click', function () { setOpen(false); });
    });
  }
  bindModal();

  // Escape key — bound once on document, unaffected by section swaps.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var modal = sectionWrapper.querySelector('[data-nutri-modal]');
    if (modal && modal.getAttribute('aria-hidden') === 'false') {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  });

  // -------- Flavor sync via Section Rendering API --------
  var variantInput = document.querySelector('[data-variant-id]');
  if (!variantInput) return; // No hero on this page — section stays static.
  var lastVariantId = variantInput.value;
  var currentController = null;

  function getProductHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function reRenderSection() {
    var currentVariantId = variantInput.value;
    if (!currentVariantId || currentVariantId === lastVariantId) return;

    // Cancel any in-flight fetch — the newer click wins.
    if (currentController) currentController.abort();
    currentController = new AbortController();
    var signal = currentController.signal;

    var productHandle = getProductHandle();
    if (!productHandle) return;

    var url = '/products/' + productHandle
            + '?variant=' + encodeURIComponent(currentVariantId)
            + '&section_id=' + encodeURIComponent(sectionId);

    fetch(url, { signal: signal, headers: { 'Accept': 'text/html' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // Preserve modal state across the swap.
        var oldModal = sectionWrapper.querySelector('[data-nutri-modal]');
        var wasOpen  = oldModal && oldModal.getAttribute('aria-hidden') === 'false';

        // Response is <div id="shopify-section-X">...contents...</div>.
        // We keep our existing wrapper (Shopify's mount point) and swap
        // its innerHTML with the fresh contents.
        var tmp = document.createElement('div');
        tmp.innerHTML = html.trim();
        var newWrapper = tmp.firstElementChild;
        if (!newWrapper) throw new Error('Empty section response');
        sectionWrapper.innerHTML = newWrapper.innerHTML;

        // Fresh DOM — rebind modal handlers.
        bindModal();

        // Reveal the swapped-in content IMMEDIATELY. innerHTML replacement
        // destroyed the .reveal nodes lc-base's IntersectionObserver was
        // watching (and had marked .on); the replacements arrive unobserved
        // and un-revealed, so without this they sit at opacity:0 forever —
        // the "blank white section" bug. No re-observe: this content is
        // mid-page and was already visible; re-animating it would flash.
        sectionWrapper.querySelectorAll('.reveal').forEach(function (el) {
          el.classList.add('on');
        });

        // Restore modal open state if it was open pre-swap.
        if (wasOpen) {
          var newModal = sectionWrapper.querySelector('[data-nutri-modal]');
          if (newModal) {
            newModal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
          }
        }

        lastVariantId = currentVariantId;
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return; // Superseded by newer fetch — expected.
        // Silent-degrade: stale NFP data beats a broken page. Logged for debugging.
        console.warn('[LC NFP] Section re-render failed:', err);
      })
      .then(function () { currentController = null; });
  }

  // Hero JS updates the hidden variant input synchronously in its click
  // handler. setTimeout 0 queues our check after that runs.
  document.addEventListener('click', function (e) {
    var picker = e.target.closest('.lc-pdp-hero__pill, .lc-pdp-hero__pack');
    if (!picker) return;
    setTimeout(reRenderSection, 0);
  });
})();
